import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Rocket, Users, DollarSign, AlertCircle, ArrowUpRight, ClipboardList } from "lucide-react";
import { useAdminAuth } from "@/context/AdminAuthContext";

const Card = ({ label, value, icon: Icon, accent = false, testId }) => (
  <div
    data-testid={testId}
    className={`rounded-2xl p-6 border transition-transform hover:-translate-y-0.5 ${accent
      ? "bg-[#0D1B2A] text-white border-[#0D1B2A]"
      : "bg-white text-[#0D1B2A] border-[#0D1B2A]/10"}`}
  >
    <div className={`inline-grid place-items-center h-10 w-10 rounded-xl ${accent ? "bg-white/10 text-[#E85D04]" : "bg-[#FFFBF5] text-[#E85D04]"}`}>
      <Icon className="h-5 w-5" />
    </div>
    <div className="mt-5 font-display text-3xl font-semibold tracking-tight">{value}</div>
    <div className={`text-xs mt-1 ${accent ? "text-white/60" : "text-[#0D1B2A]/60"}`}>{label}</div>
  </div>
);

const AdminDashboard = () => {
  const { api, admin } = useAdminAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).finally(() => setLoading(false));
  }, [api]);

  const fmtMoney = (n) => "$" + (n >= 1000 ? (n / 1000).toFixed(n >= 100000 ? 0 : 1) + "K" : n);

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="admin-dashboard-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#E85D04]">Overview</span>
          <h1 className="mt-1 font-display text-3xl md:text-4xl font-semibold tracking-tight">Hi {admin?.name || "there"}.</h1>
          <p className="mt-1 text-[#0D1B2A]/60 text-sm">Snapshot of Dwinov as of {new Date().toLocaleDateString()}.</p>
        </div>
        {stats?.pending_actions > 0 && (
          <Link to="/admin/campaigns?status=pending_review"
            data-testid="pending-actions-cta"
            className="inline-flex items-center gap-2 rounded-xl bg-[#E85D04] hover:bg-[#c94d02] text-white px-4 py-2.5 text-sm font-semibold shadow-sm">
            <AlertCircle className="h-4 w-4" /> {stats.pending_actions} pending action{stats.pending_actions > 1 ? "s" : ""}
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        )}
      </div>

      {loading ? (
        <div className="mt-8 text-[#0D1B2A]/60 text-sm">Loading…</div>
      ) : (
        <>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card label="Total GMV raised" value={fmtMoney(stats.gmv)} icon={DollarSign} accent testId="stat-gmv" />
            <Card label="Live campaigns" value={stats.campaigns.live} icon={Rocket} testId="stat-live" />
            <Card label="Pending review" value={stats.campaigns.pending_review} icon={ClipboardList} testId="stat-pending" />
            <Card label="Registered users" value={stats.users.total} icon={Users} testId="stat-users" />
          </div>

          <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="rounded-2xl bg-white border border-[#0D1B2A]/10 p-6" data-testid="stats-campaigns-breakdown">
              <h2 className="font-display text-lg font-semibold">Campaign breakdown</h2>
              <div className="mt-5 divide-y divide-[#0D1B2A]/5">
                {[
                  ["Live", stats.campaigns.live, "#E85D04"],
                  ["Pending review", stats.campaigns.pending_review, "#0D1B2A"],
                  ["Changes requested", stats.campaigns.changes_requested, "#0D1B2A"],
                  ["Rejected", stats.campaigns.rejected, "#94a3b8"],
                  ["Funded", stats.campaigns.funded, "#059669"],
                  ["Ended", stats.campaigns.ended, "#94a3b8"],
                ].map(([label, count, color]) => (
                  <div key={label} className="flex items-center justify-between py-3">
                    <span className="inline-flex items-center gap-2 text-sm text-[#0D1B2A]/80">
                      <span className="h-2 w-2 rounded-full" style={{ background: color }} /> {label}
                    </span>
                    <span className="font-display font-semibold">{count}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl bg-white border border-[#0D1B2A]/10 p-6" data-testid="stats-users-breakdown">
              <h2 className="font-display text-lg font-semibold">Users</h2>
              <div className="mt-5 grid grid-cols-2 gap-4">
                <div className="rounded-2xl bg-[#FFFBF5] p-5">
                  <div className="text-xs uppercase tracking-widest text-[#0D1B2A]/50">Creators</div>
                  <div className="mt-2 font-display text-3xl font-semibold">{stats.users.creators}</div>
                </div>
                <div className="rounded-2xl bg-[#FFFBF5] p-5">
                  <div className="text-xs uppercase tracking-widest text-[#0D1B2A]/50">Backers</div>
                  <div className="mt-2 font-display text-3xl font-semibold">{stats.users.backers}</div>
                </div>
              </div>
              <p className="mt-5 text-xs text-[#0D1B2A]/60">
                Grow the pool: creator sign-ups convert 3× better after their first pledge — enable pledge-first onboarding to lift conversion.
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminDashboard;
